import { UserAccessFeature } from "../features/auth/services/model/AuthProfile";

/**
 * Check if the user has ANY of the required permissions. This is an OR check.
 * ANY keyword is a special keyword to allow anyone
 * @returns
 */
const hasPermission = (
  userPermissions: string[],
  requiredPermissions: string[]
): boolean => {
  if (requiredPermissions.includes("ANY")) {
    return true;
  }
  return userPermissions.some((p) => requiredPermissions.includes(p));
};

/**
 * Check if the user has ANY of the required capabilities. This is an OR check.
 * ANY keyword is a special keyword to allow anyone.
 * Treats undefined userCapabilities as empty (backwards-compat with older backend).
 * @returns
 */
const hasCapability = (
  userCapabilities: string[] | undefined,
  required: string | string[]
): boolean => {
  const list = Array.isArray(required) ? required : [required];
  if (list.includes("ANY")) {
    return true;
  }
  if (!userCapabilities) {
    return false;
  }
  return list.some((cap) => userCapabilities.includes(cap));
};

/**
 * Check if the user has ANY of the required features. This is an OR check.
 * ANY keyword is a special keyword to allow anyone
 * @returns
 */
const hasFeature = (
  userFeatures: UserAccessFeature[],
  requiredFeature: string | string[]
): boolean => {
  if (requiredFeature === "ANY") {
    return true;
  }
  if (!userFeatures) {
    console.log("[Diagnostics] user_features not yet loaded");
    return false;
  }
  if (Array.isArray(requiredFeature)) {
    return requiredFeature.some((feature) =>
      userFeatures
        .filter((uf) => uf.enabled)
        .map((f) => f.feature.name)
        .includes(feature)
    );
  }
  return userFeatures
    .filter((uf) => uf.enabled)
    .map((f) => f.feature.name)
    .includes(requiredFeature);
};

export const PERMISSION_ADD_ANNOUNCEMENT = "app.add_announcement";
export const PERMISSION_VIEW_ACCOUNCEMENT = "app.view_announcement";
export const PERMISSION_DELETE_ANNOUNCEMENT = "app.delete_announcement";

export const PERMISSION_ADD_FORM = "app.add_form";
export const PERMISSION_VIEW_FORM = "app.view_form";

export const PERMISSION_ADD_PAYSLIP = "app.add_employeepayslip";
export const PERMISSION_VIEW_PAYSLIP = "app.view_employeepayslip";

export const PERMISSION_ADD_LEAVE = "app.add_employeeleave";
export const PERMISSION_VIEW_LEAVE = "app.view_employeeleave";

export const PERMISSION_VIEW_EMPLOYEE_MANAGEMENT =
  "app.view_employeemanagement";

export const FEATURE_ANNOUNCEMENTS = "Announcements";
export const FEATURE_MULTIMESSAGING = "Multi Company Messaging";
export const FEATURE_FORMS = "Forms";
export const FEATURE_PAYSLIPS = "Payslips";
export const FEATURE_ROSTERS = "Rosters";
export const FEATURE_TIMESHEETS = "Timesheets";
export const FEATURE_IRP5S = "IRP5s";
export const FEATURE_LEAVE_MANAGEMENT = "Leave Management";
export const FEATURE_ODP = "Earned Wage Access";
export const FEATURE_SELF_MANAGED_PAYSLIPS_MATCHING_CRITERIA =
  "Self Managed Payslips Matching Criteria";
export const FEATURE_PAYSLIP_QUERY = "Payslip Query";
export const FEATURE_ROSTER_QUERY = "Roster Query";
export const FEATURE_TIMESHEET_QUERY = "Timesheet Query";
export const FEATURE_EMPLOYEE_MANAGEMENT = "ANY";
export const FEATURE_INTEGRATIONS = "Integrations";
export const FEATURE_AI_INSIGHTS = "AI Insights";
export const FEATURE_SEGMENTS = "Segments";
export const FEATURE_TIME_AND_ATTENDANCE = "Time and Attendance";

export const USER_ROLE_MAPPING = {
  ADMIN: "admin",
  MANAGER: "manager",
  VIEWER: "viewer",
};

export const AUTH_PROFILE_KEY = "auth_profile";

// T&A capability codenames — bare strings matching the backend
// TACapability.codename values served via /auth/user/.ta_capabilities.
export const CAPABILITY_TA_CLOCK_WHATSAPP = "ta_clock_whatsapp";
export const CAPABILITY_TA_BULK_CLOCK_WHATSAPP = "ta_bulk_clock_whatsapp";
export const CAPABILITY_TA_VIEW_POSTING_SHEET = "ta_view_posting_sheet";
export const CAPABILITY_TA_CLOCK_HUB = "ta_clock_hub";
export const CAPABILITY_TA_VIEW_COVERAGE = "ta_view_coverage";
export const CAPABILITY_TA_BULK_CLOCK_APPROVE = "ta_bulk_clock_approve";
export const CAPABILITY_TA_MARK_NO_SHOW = "ta_mark_no_show";
export const CAPABILITY_TA_ASSIGN_REPLACEMENTS = "ta_assign_replacements";
export const CAPABILITY_TA_ASSIGN_CASUAL = "ta_assign_casual";
export const CAPABILITY_TA_VIEW_EXCEPTIONS = "ta_view_exceptions";
export const CAPABILITY_TA_OVERRIDE_CLOCK = "ta_override_clock";
export const CAPABILITY_TA_VIEW_REPORTS = "ta_view_reports";
export const CAPABILITY_TA_GENERATE_REPORTS = "ta_generate_reports";
export const CAPABILITY_TA_UPLOAD_ROSTERS = "ta_upload_rosters";
export const CAPABILITY_TA_CONFIGURE_SITES = "ta_configure_sites";
export const CAPABILITY_TA_MANAGE_USERS = "ta_manage_users";
export const CAPABILITY_TA_MANAGE_ROLES = "ta_manage_roles";
export const CAPABILITY_TA_ADMIN_SCOPE = "ta_admin_scope";

export default {
  hasPermission,
  hasFeature,
  hasCapability,
};
